import MenuBookIcon from '@mui/icons-material/MenuBook';
import DashboardIcon from '@mui/icons-material/Dashboard';
import type { SvgIconComponent } from '@mui/icons-material';
import type { NavItem } from '@/components/layout/Sidebar';
import type { ErpModule } from '@/api/platform.api';
import { getAllAppNavConfigs } from '@/config/appNavigation';
import { MODULE_ENTRIES } from '@/config/moduleEntries';
import { getModuleAdminNavItems } from '@/config/moduleDefaults';
import { masterAdminNav, orgAdminNav, posNav, lockedModuleNav } from '@/config/navigation';

/** Routes not listed in workflow nav but used in POS terminal / deep links. */
const SUPPLEMENTARY_NAV: NavItem[] = [
  { to: '/pos/shortcuts', label: 'Keyboard guide', icon: MenuBookIcon },
];

function buildNavRegistry(): NavItem[] {
  const byPath = new Map<string, NavItem>();

  const register = (items: NavItem[]) => {
    for (const item of items) {
      const key = item.to.split('?')[0];
      if (!byPath.has(key)) {
        byPath.set(key, item);
      }
    }
  };

  for (const config of getAllAppNavConfigs()) {
    for (const group of config.groups) {
      register(group.items);
    }
  }

  for (const entry of MODULE_ENTRIES) {
    register(getModuleAdminNavItems(entry.erpModule as ErpModule));
    register([{ to: entry.homePath, label: entry.label, icon: DashboardIcon, end: true }]);
  }

  register([
    ...masterAdminNav,
    ...orgAdminNav,
    ...posNav,
    ...lockedModuleNav,
    ...SUPPLEMENTARY_NAV,
  ]);

  return [...byPath.values()];
}

let cachedRegistry: NavItem[] | null = null;

function allNavItems(): NavItem[] {
  if (!cachedRegistry) {
    cachedRegistry = buildNavRegistry();
  }
  return cachedRegistry;
}

/** Match sidebar / NavLink semantics, including exact-only POS billing terminal. */
export function navItemMatchesPath(pathname: string, item: NavItem): boolean {
  const base = item.to.split('?')[0];
  if (item.end) {
    return pathname === base;
  }
  if (base === '/pos') {
    return pathname === '/pos';
  }
  return pathname === base || pathname.startsWith(`${base}/`);
}

export function resolveNavItemForPath(pathname: string): NavItem | null {
  const normalized = pathname.split('?')[0];
  let best: { item: NavItem; score: number } | null = null;

  for (const item of allNavItems()) {
    if (!navItemMatchesPath(normalized, item)) continue;
    const score = item.to.split('?')[0].length;
    if (!best || score > best.score) {
      best = { item, score };
    }
  }

  return best?.item ?? null;
}

export function resolveNavIconForPath(pathname: string): SvgIconComponent | null {
  return resolveNavItemForPath(pathname)?.icon ?? null;
}
